// "Send now" — email the configured report once, or upload it to an SFTP destination.
import { useState } from "react";
import { HardDriveUpload, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RecipientsField } from "./RecipientsField";
import { ChannelToggle, DestinationSelect, useDestinations } from "./DeliveryTarget";
import { describeParams, formatLabel } from "./reportsApi";

const fileFormats = (report) => report.formats.filter((f) => f === "csv" || f === "pdf");

export function DeliverDialog({ report, params, open, onClose, channel: initialChannel = "email" }) {
  const { user } = useAuth();
  const [channel, setChannel] = useState(initialChannel);
  const [destinationId, setDestinationId] = useState("");
  const destinations = useDestinations(true);
  const [recipients, setRecipients] = useState([]);
  const [self, setSelf] = useState(true);
  const [format, setFormat] = useState(report.formats.includes("pdf") ? "pdf" : report.formats[0]);
  const [name, setName] = useState(report.label);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const sftp = channel === "sftp";
  const formats = sftp ? fileFormats(report) : report.formats;
  const fmt = formats.includes(format) ? format : formats[0];

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/report-hub/deliver", {
        report_key: report.key, params, format: fmt, recipients, send_to_self: self, name, note,
        channel, destination_id: sftp ? destinationId : "",
      });
      if (sftp) toast.success(`Uploaded to ${data.destination} — ${data.remote_path} (${data.rows} rows)`);
      else toast.success(data.status === "paused"
        ? `Generated (${data.rows} rows) — outbound email is paused, so nothing left the platform.`
        : `Sent to ${(data.recipients || []).join(", ")} (${data.rows} rows)`);
      onClose(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || (sftp ? "Upload failed — see the History tab" : "Could not send"));
      if (sftp) onClose(true);
    } finally { setBusy(false); }
  };

  const canSend = !busy && formats.length > 0 && (sftp ? !!destinationId : (recipients.length > 0 || self));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-lg" data-testid="deliver-dialog">
        <DialogHeader>
          <DialogTitle>{sftp ? "Upload this report to SFTP now" : "Email this report now"}</DialogTitle>
          <DialogDescription>
            {report.label}{describeParams(report, params).length ? ` · ${describeParams(report, params).join(" · ")}` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <ChannelToggle value={channel} onChange={setChannel} testid="deliver-channel" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{sftp ? "Name (for the log)" : "Subject / name"}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9" data-testid="deliver-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{sftp ? "File" : "Delivery"}</Label>
              <Select value={fmt || ""} onValueChange={setFormat}>
                <SelectTrigger className="h-9" data-testid="deliver-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {formats.map((f) => <SelectItem key={f} value={f} data-testid={`deliver-format-${f}`}>{formatLabel(f, channel)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {sftp ? (
            <DestinationSelect value={destinationId} onChange={setDestinationId} destinations={destinations} testid="deliver-destination" />
          ) : (
            <>
              <RecipientsField value={recipients} onChange={setRecipients} sendToSelf={self} onSendToSelf={setSelf}
                               myEmail={user?.email} testid="deliver-recipients" />
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Note (optional)</Label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="A line for the recipients…" data-testid="deliver-note" />
              </div>
            </>
          )}
          {sftp && formats.length === 0 && (
            <p className="text-xs text-rose-600" data-testid="deliver-no-file-format">This report has no CSV or PDF form, so it cannot be uploaded.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} data-testid="deliver-cancel">Cancel</Button>
          <Button onClick={send} disabled={!canSend} data-testid="deliver-send">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : sftp ? <HardDriveUpload className="h-4 w-4" /> : <Send className="h-4 w-4" />}
            {sftp ? "Upload" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
